/**
 * A simplified marquee component for announcement bars
 * @extends {HTMLElement}
 */
class MarqueeCustom extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.wrapper = null;
    /** @type {HTMLElement | null} */
    this.content = null;
    /** @type {Node | null} */
    this.clonedContent = null;
    /** @type {number | null} */
    this.resizeTimeout = null;
  }

  connectedCallback() {
    this.wrapper = this.querySelector(".marquee__wrapper");
    this.content = this.querySelector(".marquee__content");

    if (!this.wrapper || !this.content) return;

    // Check if content exists and is not empty
    if (this.content.children.length === 0) return;

    // Only initialize if not disabled
    if (!this.isDisabled) {
      this.init();
    }

    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  init() {
    this.duplicateContent();
    // Wait for next frame to ensure DOM is updated before calculating speed
    requestAnimationFrame(() => {
      this.setSpeed();
    });
  }

  bindEvents() {
    window.addEventListener("resize", this.handleResize.bind(this));
    this.addEventListener("mouseenter", this.pauseAnimation.bind(this));
    this.addEventListener("mouseleave", this.resumeAnimation.bind(this));
  }

  unbindEvents() {
    window.removeEventListener("resize", this.handleResize.bind(this));
    this.removeEventListener("mouseenter", this.pauseAnimation.bind(this));
    this.removeEventListener("mouseleave", this.resumeAnimation.bind(this));
  }

  duplicateContent() {
    // Remove existing cloned content
    if (this.clonedContent && this.clonedContent instanceof HTMLElement) {
      this.clonedContent.remove();
    }

    // Clone the content for seamless loop
    if (this.content) {
      this.clonedContent = this.content.cloneNode(true);
      if (this.clonedContent instanceof HTMLElement) {
        this.clonedContent.setAttribute("aria-hidden", "true");
        this.clonedContent.classList.add("marquee__content--cloned");
        if (this.wrapper) {
          this.wrapper.appendChild(this.clonedContent);
        }
      }
    }
  }

  setSpeed() {
    if (!this.content || !this.wrapper) return;

    // Get the user-defined speed from CSS variable (in seconds)
    // Lower values = faster scrolling
    const userSpeed = parseFloat(
      getComputedStyle(this).getPropertyValue("--marquee-duration") || "30"
    );

    // Use the user's speed setting directly
    // The animation moves -50% of wrapper width (which contains original + cloned content)
    // This creates a seamless infinite loop
    this.style.setProperty("--marquee-calculated-speed", `${userSpeed}s`);
  }

  handleResize() {
    // Debounce resize events
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = window.setTimeout(() => {
      if (!this.isDisabled) {
        // Re-duplicate content in case content changed
        this.duplicateContent();
        // Recalculate speed after content is updated
        requestAnimationFrame(() => {
          this.setSpeed();
        });
      }
    }, 250);
  }

  pauseAnimation() {
    this.style.setProperty("--marquee-play-state", "paused");
  }

  resumeAnimation() {
    this.style.setProperty("--marquee-play-state", "running");
  }

  /**
   * Check if marquee is disabled
   * @returns {boolean}
   */
  get isDisabled() {
    return (
      this.hasAttribute("data-disabled") ||
      this.classList.contains("announcement-bar-new--disabled")
    );
  }
}

// Register the custom element
if (!customElements.get("marquee-custom")) {
  customElements.define("marquee-custom", MarqueeCustom);
}
